import React, { useCallback, memo, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';

import Traductor from '../../Traductor';
import CardBody from '../Management/BatchCard/CardBody';
import {
    isAppleDeviceOrNavegatorForDates,
    formatDate,
} from '../../../Functions';
import { formatNumber } from '../../../js/components/shared/vehicleFunctions';

import usePermissions from 'hooks/usePermissions';
import { READONLY_PERMISSION } from 'constants/Permissions';

const DetailsSummary = ({ data, loading, amountsForm, onSendProposal }) => {
    const [spinner, setSpinner] = useState(false);
    const { checkPermission } = usePermissions();
    const isMobile = useMediaQuery({ query: '(max-width: 600px)' });
    const t = useMemo(() => new Traductor(), []);
    const blocked =
        checkPermission(READONLY_PERMISSION) ||
        data?.details?.IdStatus === '2' ||
        data?.details?.IdStatus === '4' ||
        data?.details?.IdStatus === '7';
    const formEntries = useMemo(() => Object.entries(amountsForm), [
        amountsForm,
    ]);
    const formSum = useMemo(
        () => formEntries.reduce((acc, [key, value]) => acc + +value, 0),
        [formEntries]
    );
    const formNumberOfVehicles = formEntries.length;

    const checkIfAllVehiclesHaveValidImport = useCallback(() => {
        return (
            Object.values(amountsForm).every(
                amount => amount > 0 && amount % 100 === 0
            ) &&
            data?.cars.some(
                car => car.AmountOffered !== +amountsForm[car.IdPublish]
            )
        );
    }, [amountsForm, data]);

    const getBodyTotal = () => {
        if (data?.details?.Amount) {
            return `${formatNumber(data?.details?.Amount)}€`;
        }

        if (
            formSum === 0 ||
            data?.details?.NumVehicles !== formNumberOfVehicles
        )
            return '-';

        return `${formatNumber(formSum)}€`;
    };

    const renderFooter = useCallback(() => {
        if (!data || blocked) return;

        if (
            !data?.details?.Amount ||
            (data?.details?.Amount === formSum &&
                !checkIfAllVehiclesHaveValidImport())
        )
            return;

        return (
            <div className="o-layout__item  u-1/1">
                <dl className="c-dl-horizontal  u-flex">
                    <dt
                        className="c-dl-horizontal__title u-inline-item u-mb"
                        style={{ fontWeight: 700 }}
                    >
                        {t.getTraduction('NewProposedAmount')}
                    </dt>
                    <dd className="c-dl-horizontal__item u-text-right u-inline-item u-text-brand u-mb">
                        {formatNumber(formSum)}€
                    </dd>
                </dl>
            </div>
        );
    }, [data, blocked, formSum, checkIfAllVehiclesHaveValidImport, t]);

    return (
        <div
            className={`c-card o-layout__item u-1/4 u-1/1@s ${
                !isMobile
                    ? 'u-inline-item c-sticky-container  js-sticky-container u-float-right'
                    : ''
            } u-p-none`}
        >
            <div
                className={`${
                    !isMobile ? 'c-sticky-container__inner' : ''
                } u-ph`}
            >
                <div className="c-card__body" style={{ overflow: 'hidden' }}>
                    <div className="o-layout  o-layout--divider">
                        <div className="o-layout__item  u-1/1 u-pb-none">
                            <h6
                                className={`c-card__title u-text-left u-mb ${
                                    loading
                                        ? 'skeleton-details-title u-1/1 u-mb-large'
                                        : ''
                                }`}
                            >
                                {data?.details?.Name}
                            </h6>
                            <CardBody
                                loading={loading}
                                info={[
                                    [
                                        t.getTraduction('Estado'),
                                        t.getTraduction(
                                            'ManagementFiltersStatus'
                                        )[data?.details?.IdStatus],
                                    ],
                                    [
                                        t.getTraduction('CreationDate'),
                                        formatDate(
                                            isAppleDeviceOrNavegatorForDates(
                                                data?.details?.CreateDate
                                            ),
                                            false
                                        ),
                                    ],
                                    [
                                        t.getTraduction('NumVehiculos'),
                                        data?.details?.NumVehicles,
                                    ],
                                    [
                                        data?.details?.Amount
                                            ? t.getTraduction('ProposedAmount')
                                            : `${t.getTraduction(
                                                  'Total'
                                              )} ${t.getTraduction('Import')}`,
                                        getBodyTotal(),
                                    ],
                                ]}
                                idStatus={data?.details?.IdStatus}
                                amount={data?.details?.Amount}
                            />
                        </div>
                        {renderFooter()}
                    </div>
                    <button
                        className="c-btn  c-btn--full u-mt"
                        disabled={
                            loading ||
                            blocked ||
                            !checkIfAllVehiclesHaveValidImport() ||
                            spinner
                        }
                        onClick={() => {
                            onSendProposal(setSpinner);
                        }}
                    >
                        <span className="c-btn__text">
                            {!loading && data.details?.Amount
                                ? t.getTraduction('SendNewProposal')
                                : t.getTraduction('SendProposal')}
                        </span>
                        {spinner && (
                            <div
                                className="c-spinner c-spinner--small"
                                aria-live="polite"
                                aria-busy="true"
                            >
                                <div className="c-spinner__loader">
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                    <span className="c-spinner__element"></span>
                                </div>
                            </div>
                        )}
                    </button>
                    <p
                        className="u-mt"
                        style={{
                            color: 'gray',
                            textJustify: 'auto',
                            textAlign: 'initial',
                        }}
                    >
                        {t.getTraduction('BatchTransportReminder')}
                    </p>
                </div>
            </div>
        </div>
    );
};

DetailsSummary.propTypes = {
    data: PropTypes.object,
    loading: PropTypes.any,
    amountsForm: PropTypes.object,
    onSendProposal: PropTypes.func.isRequired,
};

export default memo(DetailsSummary);
